import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { Footer, Header, Meta } from '$partials/global';
import { useAjaxListing } from '$hooks/ajax-listing';
import { useLocale } from '$contexts/locale';
import { AjaxListingContext, AjaxListingPagination } from '$partials/base/ajax-listing';
import ArticleStandardListingItem from '$partials/listings/items/article-standard';
import Profile from '$partials/profiles/profile';
import { Listbox } from '../../../../node_modules/@headlessui/react/dist/index';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ProfilePage = ({ data, domain }) => {
    const { current: locale, translate: t } = useLocale();
    const [filter, setFilter] = useState(data.topics?.find(topic => topic.slug === data?.items?.filters?.topic));

    const page = data?.page;
    const listing = useAjaxListing({
        items: data?.items?.items,
        page: data?.items?.page || 1,
        path: '/api/v1/listings/articles',
        totalItems: data?.items?.total,
        totalPages: data?.items?.totalPages,
        query: { locale: locale?.code, author: page.slug, topics: filter?.slug  }
    });
    return (
        <>
            <Meta
                page={page}
                type='Article' />

            <main
                className='bg-grey min-h-screen'
                id='main-content'>
                <div className='layout-container'>
                    <Header
                        menu={data?.navigation}
                        search={data?.search}/>

                    <article className='pt-60 sm:pt-75 md:pt-100'>
                        {/* Header */}
                        <header className='px-10 sm:px-20'>
                            <h1 className='type-style-2 inline mr-10'>
                                Profile
                            </h1>
                        </header>

                        {/* Editorialist */}
                        <Profile {...page}/>

                        <p className='type-style-2 w-full sm:w-4/6 md:w-10/12 px-10 sm:px-20 mt-60 md:mt-80'>
                            {t('Related Stories')}
                        </p>
                        {data.topics?.length > 0 && (
                            <div className='type-style-7 mt-20 mb-60 md:mb-70 px-10 sm:px-20'>
                                <Listbox
                                    onChange={setFilter}
                                    value={filter}>
                                    {({ open }) => {
                                        return (
                                            <div className='relative w-full sm:w-max'>
                                                <Listbox.Button className={`select ${open ? 'open w-full sm:w-auto text-black' : 'text-orange'} flex items-center bg-white rounded type-style-7 `}>
                                                    {filter?.title}
                                                </Listbox.Button>
                                                <div
                                                    className={'absolute w-full bg-white'}>
                                                    <Listbox.Options className='flex flex-col z-10 rounded-b bg-white absolute w-full'>
                                                        {data.topics.map((topic, idx) => {
                                                            if (!topic || topic.slug === filter.slug) { return null;}
                                                            return (
                                                                <Listbox.Option
                                                                    className='px-20 flex items-center text-black hover:text-white hover:bg-orange type-style-7 py-15 cursor-pointer w-full '
                                                                    key={idx}
                                                                    value={topic}>
                                                                    {topic.title}
                                                                </Listbox.Option>
                                                            );})}
                                                    </Listbox.Options>
                                                </div>
                                            </div>
                                        );
                                    }}
                                </Listbox>
                            </div>
                        )}

                        {/* Listing */}
                        <AjaxListingContext.Provider value={listing}>
                            <section
                                aria-busy={listing.loading}
                                aria-label='Articles'
                                className='flex flex-wrap mt-40'
                                role='feed'>
                                {listing?.items?.map((item, i) => {
                                    if (!item) {return null;}
                                    return (
                                        <article
                                            aria-label={item.title}
                                            aria-posinset={i + 1}
                                            aria-setsize={listing.totalItems}
                                            className={'flex flex-col w-full sm:w-6/12 md:w-4/12 pb-50 md:pb-70 px-10 sm:px-20'}
                                            key={i}
                                            tabIndex={-1}>
                                            <ArticleStandardListingItem {...item} />
                                        </article>
                                    );
                                })}

                                <div className='border-b border-dark-grey mx-10 sm:mx-20 w-full' />
                            </section>
                            {listing.totalPages > 1 && (
                                <div className='w-full px-10 sm:px-20 mt-30 mb-20 sm:mb-0 flex items-start'>
                                    <AjaxListingPagination
                                        current={listing.page}
                                        totalPages={listing.totalPages}/>
                                </div>
                            )}
                        </AjaxListingContext.Provider>
                    </article>

                    <Footer
                        domain={domain}
                        menu={data?.footer}/>
                </div>
            </main>
        </>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    data: PropTypes.shape({
        items: PropTypes.shape({
            filters: PropTypes.object,
            items: PropTypes.array,
            page: PropTypes.number,
            total: PropTypes.number,
            totalPages: PropTypes.number,
        }),
        page: PropTypes.shape({
            title: PropTypes.string,
            modules: PropTypes.shape({
                items: PropTypes.array
            })
        }),
        topics: PropTypes.array,
        footer: PropTypes.shape({
            items: PropTypes.array
        }),
        navigation: PropTypes.object,
        search: PropTypes.object,
    }),
    domain: PropTypes.string,
};

ProfilePage.propTypes = propTypes;

export default ProfilePage;
